import MiniSearch from "minisearch";
import { fetchPrivateIndex } from "./api";
import { getHousehold } from "./household";

// On-device search index for Private mode. Document text is cached in IndexedDB per household; queries never leave the browser.
const DB = "hv-private-index";
const STORE = "docs";
const FRESH_MS = 5 * 60 * 1000;
let mini = null, docs = [], builtFor = null, lastSync = 0, syncing = null;

const idb = () => new Promise((res, rej) => {
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => r.result.createObjectStore(STORE);
  r.onsuccess = () => res(r.result);
  r.onerror = () => rej(r.error);
});
const idbGet = async (key) => { try { const d = await idb(); return await new Promise((res, rej) => { const t = d.transaction(STORE).objectStore(STORE).get(key); t.onsuccess = () => res(t.result); t.onerror = () => rej(t.error); }); } catch { return null; } };
const idbSet = async (key, val) => { try { const d = await idb(); await new Promise((res, rej) => { const t = d.transaction(STORE, "readwrite").objectStore(STORE).put(val, key); t.onsuccess = res; t.onerror = () => rej(t.error); }); } catch {} };
const idbDel = async (key) => { try { const d = await idb(); await new Promise((res) => { const t = d.transaction(STORE, "readwrite").objectStore(STORE).delete(key); t.onsuccess = res; t.onerror = res; }); } catch {} };

const key = () => `docs:${getHousehold() || "primary"}`;
const fieldsText = (f) => Object.entries(f || {}).map(([k, v]) => `${k} ${v}`).join(" ");

const build = (list) => {
  mini = new MiniSearch({ fields: ["title", "tags", "summary", "fieldsText", "text", "category"], storeFields: ["id"], searchOptions: { boost: { title: 3, tags: 2, fieldsText: 2, summary: 1.5 }, prefix: true, fuzzy: 0.15 } });
  mini.addAll(list.map((d) => ({ ...d, tags: (d.tags || []).join(" "), fieldsText: fieldsText(d.fields) })));
  docs = list;
  builtFor = key();
};

export const syncIndex = async (force = false) => {
  if (syncing) return syncing;
  syncing = (async () => {
    const k = key();
    const cached = await idbGet(k);
    const since = !force && cached?.generated_at ? cached.generated_at : "";
    const r = await fetchPrivateIndex(since);
    const keep = new Set(r.ids);
    const merged = new Map((cached?.docs || []).filter((d) => keep.has(d.id)).map((d) => [d.id, d]));
    r.docs.forEach((d) => merged.set(d.id, d));
    const list = [...merged.values()];
    await idbSet(k, { docs: list, generated_at: r.generated_at });
    build(list);
    lastSync = Date.now();
    return list;
  })().finally(() => { syncing = null; });
  return syncing;
};
export const ensureIndex = async () => {
  if (builtFor !== key() || !mini) {
    const cached = await idbGet(key());
    if (cached?.docs?.length) build(cached.docs);
    await syncIndex().catch(() => {});
  } else if (Date.now() - lastSync > FRESH_MS) syncIndex().catch(() => {});
  return docs;
};

export const indexStats = () => ({ docs: docs.length, lastSync, ready: Boolean(mini && builtFor === key()) });

export const clearIndex = async () => { await idbDel(key()); mini = null; docs = []; builtFor = null; lastSync = 0; };

const excerpt = (text, terms, max = 600) => {
  if (!text) return "";
  const low = text.toLowerCase();
  let best = -1;
  for (const t of terms) { const i = low.indexOf(t.toLowerCase()); if (i >= 0 && (best < 0 || i < best)) best = i; }
  if (best < 0) return text.slice(0, max);
  const start = Math.max(0, best - Math.floor(max / 3));
  return (start > 0 ? "…" : "") + text.slice(start, start + max) + (start + max < text.length ? "…" : "");
};

export const searchLocal = async (q, limit = 8) => {
  await ensureIndex();
  if (!mini || !q.trim()) return [];
  const terms = q.trim().split(/\s+/).filter((t) => t.length > 1);
  const byId = new Map(docs.map((d) => [d.id, d]));
  return mini.search(q).slice(0, limit).map((r) => { const d = byId.get(r.id); return d ? { ...d, score: r.score, match_snippet: excerpt(d.text, terms, 160) } : null; }).filter(Boolean);
};

// Snippets for Private Ask: the handful of documents the device picked, trimmed to what the model needs.
export const snippetsFor = async (question, limit = 6) => {
  const hits = await searchLocal(question, limit);
  const terms = question.split(/\s+/).filter((t) => t.length > 2);
  return hits.map((d) => ({ id: d.id, title: d.title, category: d.category, summary: d.summary || "", fields: d.fields || {}, excerpt: excerpt(d.text, terms, 700) }));
};
